import { Affiliate } from '@/modules/Affiliate/types'
import {
    AFFILIATE_STATUS_OPTIONS,
    AFFILIATE_TIER_OPTIONS,
} from '@/modules/Affiliate/constants'
import { useTranslation } from 'react-i18next'
import { GridInformationWithHookForm } from 'vnetwork-ui'
import SettingIcons from '@/icons/SettingIcons'
import BadgeDollarIcons from '@/icons/BadgeDollarIcons'
import dayjs from 'dayjs'
import { updateAffiliate } from '@/modules/Affiliate/services'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import * as yup from 'yup'
import Paper from '@/components/Paper'
import CellStatus, { Color } from '@/components/GokuDataGrid/CellStatus'
import MakeCopyIcons from '@/icons/MakeCopyIcons'
import { IconButton, Tooltip } from '@mui/material'
import toast from 'react-hot-toast'
import useAffiliatePolicyPermissions from '@/modules/Affiliate/hooks/useAffiliatePolicyPermissions'

interface OverviewProps {
    affiliate?: Affiliate
    refetch?: () => void
}

const updateAffiliateSchema = yup.object({
    tier: yup.string(),
    commission_percent: yup.number().min(0).max(100),
    payout_threshold: yup.number().min(0),
    status: yup.string(),
})

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        maximumFractionDigits: 0,
    }).format(value)
}

const Overview: React.FC<OverviewProps> = ({ affiliate, refetch }) => {
    const { t } = useTranslation('affiliate')
    const { canUpdateAffiliate } = useAffiliatePolicyPermissions()

    const handleEdit = async (values: any) => {
        if (!affiliate || !canUpdateAffiliate) return
        return updateAffiliate({
            affiliate_id: affiliate.affiliate_id,
            payload: values,
        })
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: handleEdit },
        {
            reactQueryKeys: QUERY_KEYS.AFFILIATES,
            successMessage: t('updated-success'),
            errorMessage: t('Something went wrong'),
            onSuccess: () => {
                refetch?.()
            },
        }
    )

    const handleCopyLink = () => {
        if (affiliate?.referral_link) {
            navigator.clipboard.writeText(affiliate.referral_link)
            toast.success(t('copied_to_clipboard'))
        }
    }

    if (!affiliate) return null

    const tierOption = AFFILIATE_TIER_OPTIONS(t)?.find(
        (item) => item?.value === affiliate.tier
    )
    const statusOption = AFFILIATE_STATUS_OPTIONS(t)?.find(
        (item) => item?.value === affiliate.status
    )

    return (
        <div className="space-y-2">
            {/* Basic Information */}
            <GridInformationWithHookForm
                loading={isPending}
                onEdit={mutateAsync}
                schema={updateAffiliateSchema as any}
                data={[
                    {
                        label: t('affiliate_id'),
                        value: affiliate.affiliate_id,
                    },
                    {
                        label: t('affiliate_name'),
                        value: affiliate.referrer_name,
                    },
                    {
                        label: t('email'),
                        value: affiliate.referrer_email,
                    },
                    {
                        label: t('phone'),
                        value: affiliate.referrer_phone || '-',
                    },
                    {
                        label: t('company'),
                        value: affiliate.referrer_company || '-',
                    },
                    {
                        label: t('referral_code'),
                        value: (
                            <span className="font-mono">
                                {affiliate.referral_code}
                            </span>
                        ),
                    },
                    {
                        label: t('referral_link'),
                        value: (
                            <div className="flex items-center gap-2">
                                <span className="text-sm truncate max-w-[300px]">
                                    {affiliate.referral_link}
                                </span>
                                <Tooltip title={t('copy')}>
                                    <IconButton
                                        size="small"
                                        onClick={handleCopyLink}
                                    >
                                        <MakeCopyIcons className="w-4 h-4" />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        ),
                    },
                    {
                        label: t('tier'),
                        value: affiliate.tier ? (
                            <CellStatus
                                label={tierOption?.label || affiliate.tier}
                                color={tierOption?.color as Color}
                                className="capitalize"
                            />
                        ) : (
                            '-'
                        ),
                        formItemProps: {
                            name: 'tier',
                            type: 'select',
                            options: AFFILIATE_TIER_OPTIONS(t),
                        },
                    },
                    {
                        // The applied rate depends on the invoice's product, so no single
                        // number can represent it here. This is the fallback rate only; the
                        // rate actually paid is shown per invoice on the Commissions tab.
                        label: t('fallback_commission'),
                        value: (
                            <span>
                                {affiliate.commission_percent}%
                                <span className="block text-xs text-gray-500">
                                    {t('fallback_commission_hint')}
                                </span>
                            </span>
                        ),
                    },
                    {
                        label: t('payout_threshold'),
                        value: formatCurrency(affiliate.payout_threshold),
                        formItemProps: {
                            name: 'payout_threshold',
                            type: 'number',
                        },
                    },
                    {
                        label: t('payout_method'),
                        value: affiliate.payout_method || '-',
                    },
                    {
                        label: t('status'),
                        value: (
                            <CellStatus
                                label={
                                    statusOption?.label ||
                                    affiliate.status ||
                                    t('active')
                                }
                                color={statusOption?.color as Color}
                                className="capitalize"
                            />
                        ),
                        formItemProps: {
                            name: 'status',
                            type: 'select',
                            options: AFFILIATE_STATUS_OPTIONS(t),
                        },
                    },
                    {
                        label: t('created_at'),
                        value: affiliate.created_at
                            ? dayjs(affiliate.created_at).format(
                                  'DD/MM/YYYY HH:mm:ss'
                              )
                            : '-',
                    },
                    {
                        label: t('updated_at'),
                        value: affiliate.updated_at
                            ? dayjs(affiliate.updated_at).format(
                                  'DD/MM/YYYY HH:mm:ss'
                              )
                            : '-',
                    },
                ]}
                className="my-2"
                head={{
                    title: t('basic_information'),
                    icon: <SettingIcons />,
                    disabledAction: !canUpdateAffiliate,
                }}
                defaultFormValues={affiliate}
            />

            {/* Stats Summary */}
            <Paper className="my-2">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 text-primary">
                        <BadgeDollarIcons />
                    </div>
                    <span className="font-semibold text-bg-primary text-sm">
                        {t('performance_summary')}
                    </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <p className="text-sm text-gray-500">
                            {t('total_clicks')}
                        </p>
                        <p className="text-2xl font-bold mt-1">
                            {affiliate.total_clicks || 0}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">
                            {t('total_signups')}
                        </p>
                        <p className="text-2xl font-bold mt-1">
                            {affiliate.total_signups || 0}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">
                            {t('total_conversions')}
                        </p>
                        <p className="text-2xl font-bold mt-1">
                            {affiliate.total_conversions || 0}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">
                            {t('total_commission')}
                        </p>
                        <p className="text-2xl font-bold mt-1 text-green-600">
                            {formatCurrency(affiliate.total_commission || 0)}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">
                            {t('total_revenue')}
                        </p>
                        <p className="text-xl font-semibold mt-1">
                            {formatCurrency(affiliate.total_revenue || 0)}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">
                            {t('unpaid_commission')}
                        </p>
                        <p className="text-xl font-semibold mt-1 text-orange-600">
                            {formatCurrency(affiliate.unpaid_commission || 0)}
                        </p>
                    </div>
                </div>
            </Paper>
        </div>
    )
}

export default Overview
